import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation } from "wouter";

export default function PrivacyPolicy() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    document.title = "Privacy Policy | The Perth Bookkeeper";
    // Set canonical tag
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link') as HTMLLinkElement;
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = 'https://theperthbookkeeper.com.au/privacy-policy';
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-[oklch(0.16_0.05_250)]">
      {/* Header Section */}
      <div className="bg-gradient-to-b from-[oklch(0.20_0.06_250)] to-[oklch(0.16_0.05_250)] border-b border-white/10">
        <div className="container py-12 lg:py-16">
          <button
            onClick={() => setLocation("/")}
            className="inline-flex items-center gap-2 text-[oklch(0.72_0.12_75)] hover:text-[oklch(0.72_0.12_75)]/80 transition-colors mb-6 font-medium text-sm"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Privacy Policy
          </h1>
          <p className="text-white/60 text-lg max-w-2xl" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            How we collect, use, and protect your personal information
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="container py-12 lg:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[oklch(0.18_0.05_250)] rounded-lg border border-white/10 p-8 lg:p-12 text-white/90" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            
            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                1. Introduction
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                The Perth Bookkeeper ("we", "us", "our", or "Company") operates the theperthbookkeeper.com.au website (the "Service").
              </p>
              <p className="text-white/80 leading-relaxed">
                This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                2. Information Collection and Use
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                We collect several different types of information for various purposes to provide and improve our Service to you.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3 text-white/95">Types of Data Collected:</h3>
              <ul className="space-y-3 mb-4">
                <li className="text-white/80">
                  <strong className="text-white">Personal Data:</strong> While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). This may include, but is not limited to:
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-white/70">
                    <li>Email address</li>
                    <li>First name and last name</li>
                    <li>Phone number</li>
                    <li>Address, State, Province, ZIP/Postal code, City</li>
                    <li>Cookies and Usage Data</li>
                  </ul>
                </li>
              </ul>
              <p className="text-white/80 leading-relaxed">
                <strong className="text-white">Usage Data:</strong> We may also collect information on how the Service is accessed and used ("Usage Data"). This may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages you visit, the time and date of your visit, the time spent on those pages, and other diagnostic data.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                3. Use of Data
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                The Perth Bookkeeper uses the collected data for various purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/80">
                <li>To provide and maintain our Service</li>
                <li>To notify you about changes to our Service</li>
                <li>To provide customer support</li>
                <li>To gather analysis or valuable information so that we can improve our Service</li>
                <li>To monitor the usage of our Service</li>
                <li>To detect, prevent and address technical issues</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                4. Security of Data
              </h2>
              <p className="text-white/80 leading-relaxed">
                The security of your data is important to us, but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                5. Changes to This Privacy Policy
              </h2>
              <p className="text-white/80 leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "effective date" at the top of this Privacy Policy.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                6. Contact Us
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-white/5 rounded p-4 border border-white/10">
                <p className="text-white/80">
                  <strong className="text-white">Email:</strong> hello@theperthbookkeeper.com.au
                </p>
                <p className="text-white/80 mt-2">
                  <strong className="text-white">Phone:</strong> 0424 200 785
                </p>
              </div>
            </section>

            <div className="border-t border-white/10 pt-8 mt-10">
              <p className="text-white/50 text-sm">
                Last updated: May 2026
              </p>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => setLocation("/")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[oklch(0.72_0.12_75)] text-white rounded-lg font-medium hover:bg-[oklch(0.72_0.12_75)]/90 transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
